import axios from 'axios'
import Algolia from './libraries/algolia'
import algoliaIndexName from './tools/algoliaIndexName'
import algoliaSearchKey from './tools/algoliaSearchKey'
import constructUrl from './tools/urlHelper'

// When Cloudflare wants to check a visitor, it answers with a challenge page
// (header cf-mitigated: challenge) instead of our JSON. A background request
// can't pass that check, so the basket, prices and account calls would all fail
// silently. A full page load can pass it, so reload the page. Only once a
// minute, so a challenge that keeps coming back can't cause a reload loop.
const CHALLENGE_RELOAD_KEY = 'accuChallengeReloadAt'
const CHALLENGE_RELOAD_INTERVAL = 60 * 1000

const isCloudflareChallenge = (error) => Boolean(
    error && error.response && error.response.headers
    && error.response.headers['cf-mitigated'] === 'challenge',
)

const reloadForChallenge = () => {
    try {
        const lastReload = Number(window.sessionStorage.getItem(CHALLENGE_RELOAD_KEY)) || 0
        if (Date.now() - lastReload < CHALLENGE_RELOAD_INTERVAL) {
            return false
        }
        window.sessionStorage.setItem(CHALLENGE_RELOAD_KEY, String(Date.now()))
    } catch (e) {
        // Without storage we can't tell whether we've just reloaded, so don't risk a loop
        return false
    }

    // Components check this to skip their own error message while the page reloads
    window.accuReloadingForChallenge = true
    window.location.reload()
    return true
}

// A reload throws away whatever the visitor has typed, so form submissions don't
// get one. Reads (GET), basket updates and choosing a delivery option do: they
// carry nothing that can't be chosen again. Nor does the order confirmation
// page (/order?step=4), which sends the purchase once per browser, so a reload
// before the tags have run would lose it. step=4 also matches the order detail
// and tracker pages, which lose nothing by not reloading.
const RELOADABLE_POSTS = ['/api/cart/', '/api/order/carrier']

const mayReloadFor = (config) => {
    const method = String((config && config.method) || 'get').toLowerCase()
    const url = String((config && config.url) || '')

    return method === 'get' || RELOADABLE_POSTS.some((path) => url.includes(path))
}

const onOrderConfirmation = () => /(^|&)step=4(&|$)/.test(window.location.search.slice(1))

axios.interceptors.response.use((response) => response, (error) => {
    if (isCloudflareChallenge(error) && mayReloadFor(error.config) && !onOrderConfirmation()) {
        reloadForChallenge()
    }

    return Promise.reject(error)
})

// Components that start together often ask for the same prices: on a product page the layout, the buy box and the
// JSON-LD each ask for the one product. They share one stock-and-price request while it's in flight (keyed by
// currency and ids, dropped once it settles, so later calls fetch afresh). Each caller parses its own copy of the
// answer, because some change it (pricingHandler adds the base tier to price_breaks).
const pricesAndStockInFlight = new Map()

const api = {
    // The options go into the axios config, so callers can pass { signal } to
    // cancel. withCredentials stays off unless asked for: same-origin calls send
    // cookies anyway, and the cross-origin stock API wants it off.
    get: async (url, data = {}, options = {}) => axios.get(constructUrl(url), {
        ...options,
        params: data,
    }),
    post: async (url, data) => axios.post(constructUrl(url), data, { withCredentials: true }),
    alpine(url, limit) {
        return {
            pageSize: limit,
            page: 1,
            results: {
                data: [],
            },
            loading: false,
            loadPage(pageNumber) {
                this.loading = true

                const constructedUrl = url.includes('?')
                    ? `${url}&`
                    : `${url}?`

                axios
                    .get(`${constructedUrl}page=${pageNumber}&limit=${limit}`)
                    .then((response) => {
                        response.data.data.forEach((item) => {
                            this.results.data.push(item)
                        })
                        this.loading = false
                    })
            },
            load() {
                this.loadPage(this.page, this.pageSize)
            },
            async loadNextPage() {
                this.loading = !this.locading
                this.loadPage(this.page + 1, this.pageSize)
            },
        }
    },
    async search(queries) {
        if (window.Algolia === undefined) {
            window.Algolia = new Algolia(process.env.MIX_ALGOLIA_APP_ID, algoliaSearchKey())
        }

        const prefixedQueries = queries.map((query) => ({
            ...query,
            indexName: algoliaIndexName(query.indexName),
        }))

        return window.Algolia.search(prefixedQueries).then((response) => response.results).catch(() => ({
            results: [],
        }))
    },
    getPricesAndStock: async (products) => {
        const currency = window.Alpine.store('currency').id
        const productIds = products.map((item) => item.id)
        const productResults = {}

        products.forEach((product) => {
            productResults[product.id] = product
        })

        const key = `${currency}:${[...productIds].sort().join(',')}`
        if (!pricesAndStockInFlight.has(key)) {
            pricesAndStockInFlight.set(key, api.get(
                `${window.accuvars.api_url}/stock-and-price`, {
                    currency,
                    products: productIds.join(','),
                }, { withCredentials: false },
            ).catch(() => api.get(
                `${window.accuvars.fallback_api_url}/stock-and-price`, {
                    currency,
                    products: productIds.join(','),
                }, { withCredentials: false },
            )).then((response) => JSON.stringify(response.data)).finally(() => {
                pricesAndStockInFlight.delete(key)
            }))
        }

        const pricesAndStock = JSON.parse(await pricesAndStockInFlight.get(key))

        pricesAndStock.forEach((item) => {
            productResults[item.id_product].data = item
        })

        return productResults
    },
    getDynamicLeadtime: async (idProduct, quantity) => {
        try {
            const response = await api.get('/m/accu/dynamicleadtime', {
                id_product: idProduct,
                quantity: quantity,
            }, { withCredentials: true })

            return response.data
        } catch (error) {
            console.error('Failed to fetch dynamic leadtime:', error)
            return null
        }
    },
    getIntercomIdentity: async () => {
        try {
            const response = await api.get('/m/accu/intercom')
            return response.data
        } catch (error) {
            console.error('Failed to fetch intercom identity:', error)
            return null
        }
    },
}

window.api = api
export default api
